import { useEffect, useRef } from 'react';
import storybg from '../assets/storybg.webp';
import { Crop, prizeArt } from './Shared';

/* ------------------------------------------------------------------
   The branded loading screen (prototype `questLoader`), shown while
   GET /golden-quest is in flight: "WIN BIG" lands, the headline prizes
   slide through underneath it and a bar fills, over the turning ray
   backdrop. The prize rail keeps cycling until the campaign is in, and
   the first pass always completes so the screen never cuts off
   mid-sequence. It then hands over (onDone → the hub mounts and starts
   its entrance underneath) and the parent sets `fading` so it dissolves
   over the hub for LOADER_EXIT_MS before being dropped.
   ------------------------------------------------------------------ */

/* the prizes on the rail, as the prototype's QL_PRIZES; renders via prizeArt */
const PRIZES = [
  { name: 'iPhone Duo', art: 'iphone' },
  { name: 'PlayStation 5', art: 'ps5' },
  { name: 'Apple Watch SE', art: 'watch' },
  { name: 'AirPods 5', art: 'airpods' },
];
const LEAD_MS = 420;   // WIN BIG has landed before the first prize arrives
const ITEM_MS = 600;   // one prize's slot on the rail
const IN_MS = 160;     // slide in / slide out
const HOLD_MS = 320;   // …and the hold in between (the prototype's 25%–75% of 640ms)
const TAIL_MS = 270;   // the last prize settles before the hand-over
const BAR_MS = 1780;   // the bar reaches 72% here (60% of the prototype's run), then waits for the data
export const LOADER_EXIT_MS = 480;

const anim = (el, keyframes, opts) => el.animate(keyframes, { fill: 'both', easing: 'cubic-bezier(.2,.8,.2,1)', ...opts });

export default function QuestLoader({ ready, fading, onDone, zoom = 1, stageHeight = 812 }) {
  const rootRef = useRef(null);
  /* read by the running sequence without restarting it */
  const readyRef = useRef(ready);
  const doneRef = useRef(onDone);
  useEffect(() => { readyRef.current = ready; }, [ready]);
  useEffect(() => { doneRef.current = onDone; }, [onDone]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    let alive = true;
    let timer = 0;
    const wait = (ms) => new Promise((resolve) => { timer = setTimeout(resolve, ms); });
    const bar = root.querySelector('.gq-qbar i');
    const items = Array.from(root.querySelectorAll('.gq-qitem'));

    const fill = anim(bar, [{ width: '0%' }, { width: '72%' }], { duration: BAR_MS, easing: 'cubic-bezier(.3,.6,.4,1)' });
    anim(root.querySelector('.gq-qwin'), [
      { transform: 'scale(1.5)', opacity: 0, letterSpacing: '.3em' },
      { transform: 'scale(.96)', opacity: 1, offset: 0.7 },
      { transform: 'scale(1)', opacity: 1, letterSpacing: '.04em' },
    ], { duration: 520, delay: 150, easing: 'cubic-bezier(.2,1,.3,1)' });

    (async () => {
      await wait(LEAD_MS);
      let shown = 0;
      for (let i = 0; alive; i = (i + 1) % items.length) {
        const it = items[i];
        it.getAnimations({ subtree: true }).forEach((a) => a.cancel()); // its previous pass, on a long load
        anim(it, [{ transform: 'translateX(70px)', opacity: 0 }, { transform: 'translateX(0)', opacity: 1 }], { duration: IN_MS, easing: 'cubic-bezier(.3,0,.3,1)' });
        anim(it.querySelector('.gq-im'), [
          { transform: 'scale(.85) rotate(-6deg)' },
          { transform: 'scale(1.04) rotate(2deg)', offset: 0.4 },
          { transform: 'scale(1) rotate(0)' },
        ], { duration: ITEM_MS, easing: 'cubic-bezier(.2,1,.3,1)' });
        shown += 1;
        await wait(IN_MS + HOLD_MS);
        if (!alive) return;
        /* the data is in and the rail has made a full pass: this prize stays, the bar completes, hand over */
        if (readyRef.current && shown >= items.length) {
          const from = getComputedStyle(bar).width;
          fill.cancel();
          anim(bar, [{ width: from }, { width: '100%' }], { duration: TAIL_MS, easing: 'ease-out' });
          await wait(TAIL_MS);
          if (alive && doneRef.current) doneRef.current();
          return;
        }
        anim(it, [{ transform: 'translateX(0)', opacity: 1 }, { transform: 'translateX(-70px)', opacity: 0 }], { duration: IN_MS, easing: 'cubic-bezier(.3,0,.3,1)' });
        await wait(ITEM_MS - IN_MS - HOLD_MS); // the next one arrives as this one leaves
      }
    })();

    return () => {
      alive = false;
      clearTimeout(timer);
      root.getAnimations({ subtree: true }).forEach((a) => { if (!(window.CSSAnimation && a instanceof window.CSSAnimation)) a.cancel(); });
    };
  }, []);

  /* the 812px design puts WIN BIG at 214 and the bar at 712; keep those proportions on shorter stages */
  const sceneTop = Math.round(stageHeight * (214 / 812));
  const footTop = Math.round(stageHeight * (712 / 812));

  return (
    <div className={`gq-loader${fading ? ' gq-fade' : ''}`} ref={rootRef} role="status" aria-busy="true" aria-live="polite">
      <p className="gq-sr">Loading The Golden Quest…</p>
      <div className="gq-ql" style={{ zoom, height: stageHeight }} aria-hidden="true">
        <div className="gq-qbg"><img src={storybg} alt="" /></div>
        <div className="gq-qscene" style={{ top: sceneTop }}>
          <div className="gq-qglow" />
          <div className="gq-qwin">WIN BIG</div>
          <div className="gq-qprize">
            {PRIZES.map((p) => {
              const art = prizeArt(p.art);
              return (
                <div className="gq-qitem" key={p.name}>
                  <div className="gq-im"><Crop kind={art.kind} src={art.src} /></div>
                  <div className="gq-nm">{p.name}</div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="gq-qfoot" style={{ top: footTop }}>
          <div className="gq-qbar"><i /></div>
          <div className="gq-qtxt">Loading your quest…</div>
        </div>
      </div>
    </div>
  );
}
